import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { StampOutboxSync } from "@/components/StampOutboxSync";
import { OfflineSync } from "@/components/OfflineSync";
import OwnerGuard from "@/components/OwnerGuard";
import PropertyAccessGuard from "@/components/PropertyAccessGuard";
import PropertyDetailRoute from "./pages/PropertyDetailRoute";
import EditPropertyRoute from "./pages/EditPropertyRoute";
import Index from "./pages/Index";
import Login from "./pages/Login";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import PropertiesList from "./pages/owner/PropertiesList";
import NewPropertyWizard from "./pages/owner/NewPropertyWizard";
import PropertyDetail from "./pages/owner/PropertyDetail";
import Notifications from "./pages/owner/Notifications";
import Inbox from "./pages/owner/Inbox";
import IssuesInbox from "./pages/owner/IssuesInbox";
import EditProperty from "./pages/owner/EditProperty";
import ArchivedProperties from "./pages/owner/ArchivedProperties";
import CleanerPortal from "./pages/cleaner/CleanerPortal";
import TaskRunPage from "./pages/TaskRunPage";
import CleanerPortalToken from "./pages/cleaner/CleanerPortalToken";
import CleanerUsePortalLink from "./pages/cleaner/CleanerUsePortalLink";
import CleanerDashboard from "./pages/cleaner/CleanerDashboard";
import CleanerOnboarding from "./pages/cleaner/CleanerOnboarding";
import CleanerPurchases from "./pages/cleaner/CleanerPurchases";
import RegisterCleaner from "./pages/cleaner/RegisterCleaner";
import CompanyUnavailable from "./pages/company/CompanyUnavailable";
import WorkerSchedule from "./pages/worker/WorkerSchedule";
import CalendarPage from "./pages/CalendarPage";
import CalendarGuard from "./components/CalendarGuard";
import NotFound from "./pages/NotFound";
import GuestReportPage from "./pages/GuestReportPage";
import VendorWorkOrderPage from "./pages/vendor/VendorWorkOrderPage";
import { SingleTaskGuard } from "./components/SingleTaskGuard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import OnboardingDecision from "./pages/OnboardingDecision";
import CreateWorkspace from "./pages/CreateWorkspace";
import InvitePage from "./pages/InvitePage";
import InviteCompletePage from "./pages/InviteCompletePage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import Account from "./pages/Account";
import PayoutsPage from "./pages/account/PayoutsPage";
import PaymentsPage from "./pages/account/PaymentsPage";
import { ActivePropertyProvider } from "./contexts/ActivePropertyContext";
import { ViewContextProvider } from "./contexts/ViewContext";
import { WorkspaceRoleSync } from "./components/layout/WorkspaceRoleSync";
import { TurnoverTaskModalProvider } from "./contexts/TurnoverTaskModalContext";
import { TurnoverTaskModal } from "./components/owner/TurnoverTaskModal";

const queryClient = new QueryClient({
  defaultOptions: {
    // Slice 56: job saves run with no signal (they are kept on the phone), and loaded job data stays for a day.
    queries: { gcTime: 24 * 60 * 60 * 1000 },
    mutations: { networkMode: "always" },
  },
});

function RedirectOwnerPropertyToUnified() {
  const { propertyId } = useParams<{ propertyId: string }>();
  return <Navigate to={propertyId ? `/properties/${propertyId}` : "/owner/properties"} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ActivePropertyProvider>
            <ViewContextProvider>
            <WorkspaceRoleSync />
            <StampOutboxSync />
            <OfflineSync />
            <TurnoverTaskModalProvider>
              <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Post-email-confirmation for invite signups: session + accept invite + redirect into property */}
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Invite acceptance (unified invite model) */}
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/invite/complete" element={<InviteCompletePage />} />
            
            {/* Onboarding Routes - owner first-time */}
            <Route path="/onboarding/decision" element={<OnboardingDecision />} />
            <Route path="/onboarding/create-workspace" element={<CreateWorkspace />} />
            
            {/* Owner Routes - Protected */}
            <Route path="/owner/properties" element={
              <OwnerGuard>
                <PropertiesList />
              </OwnerGuard>
            } />
            <Route path="/properties/new" element={
              <OwnerGuard>
                <NewPropertyWizard />
              </OwnerGuard>
            } />
            {/* Unified property detail: owner + team admin (permission-gated tabs) */}
            <Route path="/properties/:propertyId" element={
              <PropertyAccessGuard>
                <PropertyDetailRoute />
              </PropertyAccessGuard>
            } />
            {/* Backwards compat: redirect to unified property route */}
            <Route path="/owner/properties/:propertyId" element={<RedirectOwnerPropertyToUnified />} />
            {/* Unified edit: owner or team admin with full access (guard + EditProperty redirects Limited) */}
            <Route path="/properties/:propertyId/edit" element={
              <PropertyAccessGuard>
                <EditPropertyRoute />
              </PropertyAccessGuard>
            } />
            <Route path="/owner/properties/:propertyId/edit" element={
              <OwnerGuard>
                <EditProperty />
              </OwnerGuard>
            } />
            <Route path="/owner/properties/archived" element={
              <OwnerGuard>
                <ArchivedProperties />
              </OwnerGuard>
            } />
            <Route path="/owner/notifications" element={
              <OwnerGuard>
                <Notifications />
              </OwnerGuard>
            } />
            <Route path="/owner/inbox" element={
              <OwnerGuard>
                <Inbox />
              </OwnerGuard>
            } />
            <Route path="/owner/issues" element={
              <OwnerGuard>
                <IssuesInbox />
              </OwnerGuard>
            } />

            {/* Account (owner + team admin + cleaner) */}
            <Route path="/account" element={<Account />} />
            <Route path="/account/payouts" element={<PayoutsPage />} />
            <Route path="/account/payments" element={<PaymentsPage />} />

            <Route path="/team/dashboard" element={<Navigate to="/owner/properties" replace />} />

            {/* Unified multi-property calendar (owner + team admin only) */}
            <Route path="/calendar" element={
              <CalendarGuard>
                <CalendarPage />
              </CalendarGuard>
            } />

            {/* Worker/Cleaner schedule (Part 5) - unified login */}
            <Route path="/worker/schedule" element={<WorkerSchedule />} />

            {/* Cleaner Routes - legacy / backward compat */}
            <Route path="/cleaner/dashboard" element={<CleanerDashboard />} />
            <Route path="/cleaner/onboarding" element={<CleanerOnboarding />} />
            <Route path="/cleaner/purchases" element={<CleanerPurchases />} />
            <Route path="/cleaner/inbox" element={<Inbox />} />
            <Route path="/cleaner/account" element={<Navigate to="/account" replace />} />
            {/* Register-cleaner: replaced by token link; show message to use link from owner */}
            <Route path="/register-cleaner/:propertyId" element={<CleanerUsePortalLink />} />
            {/* Token-gated cleaner portal (no auth) */}
            <Route path="/c/:token" element={<CleanerPortalToken />} />
            {/* Public guest report link (no auth): short /report/:code or long /r/:token */}
            <Route path="/report/:token" element={<GuestReportPage />} />
            <Route path="/r/:token" element={<GuestReportPage />} />
            {/* Vendor work order (no auth): token-only access */}
            <Route path="/vendor/work-order/:token" element={<VendorWorkOrderPage />} />
            <Route path="/cleaner/use-portal-link" element={<CleanerUsePortalLink />} />
            {/* Task execution route - ErrorBoundary captures white-screen errors for debugging */}
            <Route path="/clean/:propertyId" element={
              <ErrorBoundary showDebugInFallback>
                <SingleTaskGuard>
                  <CleanerPortal />
                </SingleTaskGuard>
              </ErrorBoundary>
            } />
            {/* Owner/team task runner (no View as cleaner) */}
            <Route path="/tasks/run/:propertyId" element={
              <ErrorBoundary showDebugInFallback>
                <TaskRunPage />
              </ErrorBoundary>
            } />

            {/* Company routes - legacy */}
            <Route path="/company/dashboard" element={<CompanyUnavailable />} />
            <Route path="/company/cleaners/:cleanerId" element={<CompanyUnavailable />} />
            <Route path="/company/inbox" element={<CompanyUnavailable />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
              </Routes>
              <TurnoverTaskModal />
            </TurnoverTaskModalProvider>
            </ViewContextProvider>
          </ActivePropertyProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
